import { AlertTriangle, ShieldAlert } from "lucide-react";

const PARAM_ORDER = [
  "company_no", "job_no", "description", "division_account_no", "division2_account_no", "dept_no", "cost_basis",
  "customer_no", "project_manager_no", "project_class_no", "tax_ap_no", "tax_ar_no",
  "original_contract", "original_cost", "retainage_percent", "job_start_date", "completion_date", "customer_order_no",
  "address_1", "address_2", "city", "state", "zip_code",
];

// Shared by the confirm dialog and the result card: every parameter that
// will be / was sent, the fields withheld (with reasons) and any blockers.
export default function FoundationParamsView({ params = {}, withheld = [], blockers = [], truncated = [], omittedUnchanged = [], database, isUpdate, testIdPrefix = "sp" }) {
  const keys = [...PARAM_ORDER.filter(k => k in params), ...Object.keys(params).filter(k => !PARAM_ORDER.includes(k))];
  return (
    <div className="space-y-2 text-xs" data-testid={`${testIdPrefix}-params-view`}>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-700">{isUpdate ? "Update — changed / new parameters" : "Parameters to send"} ({keys.length})</span>
        <span className="font-mono text-slate-500" data-testid={`${testIdPrefix}-params-db`}>→ {database}</span>
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 max-h-48 overflow-y-auto">
        {keys.map(k => (
          <div key={k} className="flex gap-2 px-2 py-0.5 border-b border-slate-100 last:border-b-0" data-testid={`${testIdPrefix}-param-${k}`}>
            <code className="w-44 shrink-0 text-slate-500">@{k}</code>
            <code className="text-slate-900 break-all">{String(params[k])}</code>
          </div>
        ))}
        {keys.length === 0 && <div className="px-2 py-1 text-slate-400">Nothing to send.</div>}
      </div>
      {blockers.length > 0 && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-2 space-y-0.5" data-testid={`${testIdPrefix}-blockers`}>
          <div className="flex items-center gap-1 font-semibold text-rose-800"><ShieldAlert className="w-3.5 h-3.5" /> Push blocked</div>
          {blockers.map((b, i) => <div key={i} className="text-rose-800">{b}</div>)}
        </div>
      )}
      {withheld.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-0.5" data-testid={`${testIdPrefix}-withheld`}>
          <div className="flex items-center gap-1 font-semibold text-amber-800"><AlertTriangle className="w-3.5 h-3.5" /> Withheld (sent as NULL — Foundation defaults apply)</div>
          {withheld.map((w, i) => (
            <div key={i} className="text-amber-900" data-testid={`${testIdPrefix}-withheld-${String(w.param).split("/")[0]}`}>
              <code>@{w.param}</code> — {w.reason}
            </div>
          ))}
        </div>
      )}
      {truncated.length > 0 && (
        <div className="text-amber-700" data-testid={`${testIdPrefix}-truncated-list`}>
          Truncated: {truncated.map(t => `@${t.param} ${t.from}→${t.to}`).join(", ")}
        </div>
      )}
      {omittedUnchanged.length > 0 && (
        <div className="text-slate-500" data-testid={`${testIdPrefix}-omitted-unchanged`}>
          Unchanged since last push (not re-sent): {omittedUnchanged.map(k => `@${k}`).join(", ")}
        </div>
      )}
    </div>
  );
}
